// src/redux/actions/contractActions.js
import { backendClient } from "../../api/backend";
import { EventSourcePolyfill } from 'event-source-polyfill';
import eventSourceManager from '../../utils/eventSourceManager';
import i18n from "../../i18n";
import { getDocTypesForCountry } from "../../constants/legalDocumentTypes";

import {
    SET_CONTRACT_LOADING,
    UPDATE_CONTRACT_CONTENT,
    SET_CONTRACT_DATA,
    SET_CONTRACT_ERROR,
    SET_HIGHLIGHT_ERROR,
    RESET_CONTRACT,
    SAVE_CONTRACT_REQUEST,
    SAVE_CONTRACT_SUCCESS,
    SAVE_CONTRACT_FAILURE,
    FETCH_CONTRACT_HISTORY_REQUEST,
    FETCH_CONTRACT_HISTORY_SUCCESS,
    FETCH_CONTRACT_HISTORY_FAILURE,
    ADD_HISTORY_ENTRY,
    FETCH_CONTRACT_ACTIVITY_REQUEST,
    FETCH_CONTRACT_ACTIVITY_SUCCESS,
    FETCH_CONTRACT_ACTIVITY_FAILURE,
    SHARE_CONTRACT_REQUEST,
    SHARE_CONTRACT_SUCCESS,
    SHARE_CONTRACT_FAILURE,
    DELETE_CONTRACTS_REQUEST,
    DELETE_CONTRACTS_SUCCESS,
    DELETE_CONTRACTS_FAILURE,
    FETCH_CONTRACTS_REQUEST,
    FETCH_CONTRACTS_SUCCESS,
    FETCH_CONTRACTS_FAILURE,
    FETCH_CONTRACTS_BY_TAB_REQUEST,
    FETCH_CONTRACTS_BY_TAB_SUCCESS,
    FETCH_CONTRACTS_BY_TAB_FAILURE,
    SET_TAB_TOTAL_COUNT,
    SET_CURRENT_TAB,
    SET_TAB_PAGE,
    SEARCH_CONTRACTS_REQUEST,
    SEARCH_CONTRACTS_SUCCESS,
    SEARCH_CONTRACTS_FAILURE,
    // Contract message action types
    CONTRACT_MESSAGE_CLEAR,
    CONTRACT_MESSAGE_SET,
    CONTRACT_MESSAGE_ADD_USER,
    CONTRACT_MESSAGE_STREAM_UPDATE,
    CONTRACT_MESSAGE_STREAM_DONE,
    CONTRACT_MESSAGE_ERROR,

    // New action types for conversation management
    CLEAR_CHAT_REQUEST,
    CLEAR_CHAT_SUCCESS,
    CLEAR_CHAT_FAILURE,
    CREATE_CONTRACT_CONVERSATION_REQUEST,
    CREATE_CONTRACT_CONVERSATION_SUCCESS,
    CREATE_CONTRACT_CONVERSATION_FAILURE,

    // Rich execution tracking types
    CONTRACT_CHAT_UPDATE_MESSAGE,
    CONTRACT_CHAT_UPDATE_TOOLS,
    CONTRACT_CHAT_ADD_TOOL_EXECUTION,
    CONTRACT_CHAT_UPDATE_NODE_EXECUTION,
    CONTRACT_CHAT_UPDATE_PLAN,
    CONTRACT_CHAT_UPDATE_STEP_EXECUTION,
    CONTRACT_CHAT_UPDATE_SOURCES,
    CONTRACT_CHAT_UPDATE_METADATA,
    CONTRACT_CHAT_UPDATE_EDITS,
    CONTRACT_CHAT_UPDATE_EDIT_STATUS,
    CONTRACT_CHAT_UPDATE_REVIEW_SUGGESTIONS,
    CONTRACT_CHAT_UPDATE_REVIEW_SUGGESTION_STATUS,
    CONTRACT_CHAT_UPDATE_REVIEW_PROGRESS,
    RISK_ANALYSIS_RESET,
    RISK_ANALYSIS_ADD,
    RISK_ANALYSIS_ERROR,
    RISK_ANALYSIS_COMPLETE,
    RISK_ANALYSIS_REQUEST,
    UPDATE_CONTRACT_STATUS,
    UPDATE_CONTRACT_STATUS_SUCCESS,
    UPDATE_CONTRACT_COUNTRY_SUCCESS,
    REORDER_KANBAN_CONTRACTS,
    UPDATE_CONTRACT_STATUS_FAILURE,
    CLAUSES_REQUEST,
    CLAUSES_RESET,
    CLAUSES_ADD,
    CLAUSES_ERROR,
    CLAUSES_COMPLETE,
    SUGGESTED_CLAUSES_REQUEST,
    SUGGESTED_CLAUSES_RESET,
    SUGGESTED_CLAUSES_ADD,
    SUGGESTED_CLAUSES_ERROR,
    SUGGESTED_CLAUSES_COMPLETE,
    FILTER_CONTRACTS_REQUEST,
    FILTER_CONTRACTS_SUCCESS,
    FILTER_CONTRACTS_FAILURE,
    UPDATE_CONTRACT_OPTIMISTIC,
    // Contract Categories
    FETCH_CONTRACT_CATEGORIES_REQUEST,
    FETCH_CONTRACT_CATEGORIES_SUCCESS,
    FETCH_CONTRACT_CATEGORIES_FAILURE,
    RESTORE_CONTRACT_VERSION,

    // Key Info
    KEY_INFO_REQUEST,
    KEY_INFO_FETCH_SUCCESS,
    KEY_INFO_EXTRACT_REQUEST,
    KEY_INFO_EXTRACT_UPDATE,
    KEY_INFO_EXTRACT_COMPLETE,
    KEY_INFO_ERROR,
    KEY_INFO_UPDATE_FIELD,
    KEY_INFO_RESET,

    // Law Dependencies
    LAW_DEPS_REQUEST,
    LAW_DEPS_SUCCESS,
    LAW_DEPS_ERROR,

    // Regulatory Alerts
    REG_ALERTS_REQUEST,
    REG_ALERTS_SUCCESS,
    REG_ALERTS_ERROR,
    REG_ALERT_UPDATE,

    // Workspaces & Folders
    WORKSPACES_REQUEST,
    WORKSPACES_SUCCESS,
    WORKSPACES_FAILURE,
    FOLDERS_REQUEST,
    FOLDERS_SUCCESS,
    FOLDERS_FAILURE,
    SMART_VIEWS_REQUEST,
    SMART_VIEWS_SUCCESS,
    SMART_VIEWS_FAILURE,
    SET_ACTIVE_WORKSPACE,
    SET_ACTIVE_FOLDER,
    SET_ACTIVE_SMART_VIEW,
    CREATE_WORKSPACE_SUCCESS,
    CREATE_FOLDER_SUCCESS,
    CREATE_SMART_VIEW_SUCCESS,
    DELETE_SMART_VIEW_SUCCESS,
    SMART_VIEW_COUNTS_SUCCESS,

    // Monitoring
    SET_ACTIVE_MONITORING_VIEW,
    FETCH_MONITORING_ALERTS_REQUEST,
    FETCH_MONITORING_ALERTS_SUCCESS,
    FETCH_MONITORING_ALERTS_FAILURE,
    FETCH_MONITORING_LIFECYCLE_REQUEST,
    FETCH_MONITORING_LIFECYCLE_SUCCESS,
    FETCH_MONITORING_LIFECYCLE_FAILURE,

} from "./types";
import { backendUrl } from "../../config";
import { Description } from "@mui/icons-material";

// Action Creators
export const resetContract = () => async (dispatch) => {
    dispatch({ type: RESET_CONTRACT })
}

export const updateContractContent = (content) => ({
    type: UPDATE_CONTRACT_CONTENT,
    payload: content
});

export const setContractData = (contract) => ({
    type: SET_CONTRACT_DATA,
    payload: contract
});

export const setLoading = (isLoading) => ({
    type: SET_CONTRACT_LOADING,
    payload: isLoading
});

export const setError = (error) => ({
    type: SET_CONTRACT_ERROR,
    payload: error
});




// Fetch contracts
export const fetchContracts = (userId, limit, offset, forceRefresh = false) => async (dispatch, getState) => {
    // Get current state
    const state = getState();
    const cacheKey = `${offset}_${limit}`;

    // Safely check if contractsCache exists and has the data we need
    const contractsCache = state.contract?.contractsCache || {};
    const cachedContracts = contractsCache[cacheKey];

    // Use cache only if available AND not forcing refresh
    if (cachedContracts && !forceRefresh) {
        // Use cached data if available
        dispatch({
            type: FETCH_CONTRACTS_SUCCESS,
            payload: cachedContracts,
            meta: { offset, limit }
        });
        return cachedContracts;
    }

    // Otherwise fetch from API
    dispatch({ type: FETCH_CONTRACTS_REQUEST });
    try {
        const contracts = await backendClient.getUserContracts(userId, limit, offset);
        dispatch({
            type: FETCH_CONTRACTS_SUCCESS,
            payload: contracts,
            meta: { offset, limit }
        });
        return contracts;
    } catch (error) {
        dispatch({
            type: FETCH_CONTRACTS_FAILURE,
            payload: error.message
        });
        return [];
    }
};

// Fetch contracts by tab with pagination
export const fetchContractsByTab = (tab, limit, offset, forceRefresh = false) => async (dispatch, getState) => {
    dispatch({ type: FETCH_CONTRACTS_BY_TAB_REQUEST });

    try {
        const contracts = await backendClient.getContractsByTab(tab, limit, offset);

        dispatch({
            type: FETCH_CONTRACTS_BY_TAB_SUCCESS,
            payload: contracts,
            meta: { tab, offset, limit }
        });

        return contracts;
    } catch (error) {
        dispatch({
            type: FETCH_CONTRACTS_BY_TAB_FAILURE,
            payload: error.message
        });
        return [];
    }
};

// Fetch total count for a specific tab
export const fetchTabTotalCount = (tab) => async (dispatch) => {
    try {
        const { totalContracts } = await backendClient.getContractsCountByTab(tab);

        // Map tab string to tab index
        const tabMap = {
            'all': 0,
            'recent': 1,
            'shared': 2,
            'drafts': 3,
            'in_review': 4,
            'pending_signature': 5,
            'signed': 6
        };

        const tabIndex = tabMap[tab];

        dispatch({
            type: SET_TAB_TOTAL_COUNT,
            payload: { tab: tabIndex, totalContracts }
        });

        return totalContracts;
    } catch (error) {
        console.error('Error fetching tab total count:', error);
        return 0;
    }
};

// Set current active tab
export const setCurrentTab = (tab) => ({
    type: SET_CURRENT_TAB,
    payload: tab
});

// Set page for a specific tab
export const setTabPage = (tab, page) => ({
    type: SET_TAB_PAGE,
    payload: { tab, page }
});


// Async Actions
export const fetchContract = (contractId) => {
    return async (dispatch) => {
        dispatch(setLoading(true));
        try {
            const contractData = await backendClient.getContract(contractId);
            console.log('contract_fetched', contractData)
            dispatch(setContractData(contractData));
        } catch (error) {
            console.error('Error fetching contract:', error);
            dispatch(setError(error.message || 'Failed to load contract'));
        } finally {
            dispatch(setLoading(false));
        }
    };
};

export const saveContract = (contractId, content) => {
    return async (dispatch) => {
        try {
            await backendClient.updateContract(contractId, { content });
            return true;
        } catch (error) {
            console.error('Error saving contract:', error);
            dispatch(setError(error.message || 'Failed to save contract'));
            return false;
        }
    };
};


export const fetchContractHistory = (contractId) => async (dispatch) => {
    try {
        dispatch({ type: FETCH_CONTRACT_HISTORY_REQUEST });

        // Using backendClient instead of api
        const response = await backendClient.get(`api/contract/history/${contractId}`);
        const data = await response.json();

        dispatch({
            type: FETCH_CONTRACT_HISTORY_SUCCESS,
            payload: data
        });

        return data;
    } catch (error) {
        dispatch({
            type: FETCH_CONTRACT_HISTORY_FAILURE,
            payload: error.message || 'Failed to fetch contract history'
        });
        throw error;
    }
};

// Action to add a new history entry (for real-time updates)
export const addHistoryEntry = (entry) => ({
    type: ADD_HISTORY_ENTRY,
    payload: entry
});

// Unified activity feed (history + comments + suggestions)
export const fetchContractActivity = (contractId) => async (dispatch) => {
    try {
        dispatch({ type: FETCH_CONTRACT_ACTIVITY_REQUEST });

        const response = await backendClient.get(`api/contract/activity/${contractId}`);
        const data = await response.json();

        dispatch({
            type: FETCH_CONTRACT_ACTIVITY_SUCCESS,
            payload: data
        });

        return data;
    } catch (error) {
        dispatch({
            type: FETCH_CONTRACT_ACTIVITY_FAILURE,
            payload: error.message || 'Failed to fetch contract activity'
        });
        throw error;
    }
};

// Action to restore from a historical snapshot
export const restoreFromSnapshot = (contractId, snapshotId) => async (dispatch) => {
    try {
        const payload = { snapshot_id: snapshotId };
        const response = await backendClient.post(`api/contract/restore/${contractId}`, payload);
        const data = await response.json();

        // Pass restored content directly to DocumentEditor via Redux payload.
        // We intentionally do NOT update the contract object in Redux here —
        // that would trigger a full re-render of ContractEditionScreen (permissions
        // check, page settings, pagination) causing a visible page flash.
        dispatch({ type: RESTORE_CONTRACT_VERSION, payload: data.content });

        // Refresh activity feed in background
        dispatch(fetchContractActivity(contractId));

        return data;
    } catch (error) {
        dispatch({
            type: SAVE_CONTRACT_FAILURE,
            payload: error.message || 'Failed to restore from snapshot'
        });
        throw error;
    }
};


export const shareContract = (contractId, selectedUsers, userDomain) => async (dispatch) => {
    try {
        dispatch({ type: SHARE_CONTRACT_REQUEST });


        const users = selectedUsers.map(user => ({
            user_id: user.id,
            role: user.role,
        }));
        // Using backendClient to share the contract
        const response = await backendClient.shareContract(contractId, users, userDomain);

        console.log('responsxxxe', response)

        dispatch({
            type: SHARE_CONTRACT_SUCCESS,
            payload: {
                contractId,
                collaborators: selectedUsers,
            }
        });

        return response;
    } catch (error) {
        dispatch({
            type: SHARE_CONTRACT_FAILURE,
            payload: error.message || 'Failed to share contract'
        });
        throw error;
    }
};


export const saveContractWithRetry = (contractId, content) => async (dispatch) => {
    let attempts = 0;
    const maxAttempts = 3;
    const backoffMs = 1000; // Start with 1 second

    const attemptSave = async () => {
        try {
            attempts++;
            return await dispatch(saveContract(contractId, content));
        } catch (error) {
            console.error(`Save attempt ${attempts} failed:`, error);

            if (attempts < maxAttempts) {
                // Calculate backoff with jitter
                const jitter = Math.random() * 0.3 * backoffMs;
                const delay = backoffMs * Math.pow(1.5, attempts - 1) + jitter;

                console.log(`Retrying in ${Math.round(delay / 1000)}s...`);

                // Wait and retry
                return new Promise(resolve => {
                    setTimeout(() => resolve(attemptSave()), delay);
                });
            } else {
                // All attempts failed, rethrow the error
                throw error;
            }
        }
    };

    return attemptSave();
};

export const deleteContracts = (contractIds, userId) => async (dispatch) => {

    dispatch({ type: DELETE_CONTRACTS_REQUEST });

    try {
        // Make sure your backendClient.deleteContracts accepts both params
        const response = await backendClient.deleteContracts(contractIds, userId);

        // Pass both the contractIds and userId to the success action
        dispatch({
            type: DELETE_CONTRACTS_SUCCESS,
            payload: {
                contractIds,
                userId
            }
        });

        return response;
    } catch (error) {
        console.error('Error deleting contracts:', error);

        dispatch({
            type: DELETE_CONTRACTS_FAILURE,
            payload: error.message || 'Failed to delete contracts'
        });

        throw error;
    }
};
// Search contracts
export const searchContracts = (userId, searchTerm) => async (dispatch) => {
    try {
        dispatch({ type: SEARCH_CONTRACTS_REQUEST });

        const contracts = await backendClient.searchContracts(userId, searchTerm);

        dispatch({
            type: SEARCH_CONTRACTS_SUCCESS,
            payload: contracts
        });

        return contracts;
    } catch (error) {
        console.error('Error searching contracts:', error);
        dispatch({
            type: SEARCH_CONTRACTS_FAILURE,
            payload: error.message || 'Failed to search contracts'
        });
        throw error;
    }
};



////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////// CONVERSATION ////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////


export const clearContractMessages = () => ({
    type: CONTRACT_MESSAGE_CLEAR
});

export const setContractMessages = (messages) => ({
    type: CONTRACT_MESSAGE_SET,
    payload: messages
});


export const addUserMessage = (content) => {
    return {
        type: CONTRACT_MESSAGE_ADD_USER,
        payload: {
            content: content,
            role: 'user',
            status: 'success',
            created_at: new Date().toISOString(),
        },
    };
};

export const streamAssistantUpdate = (message) => ({
    type: CONTRACT_MESSAGE_STREAM_UPDATE,
    payload: {
        id: message.id,
        content: message.content,
        sender: "bot",
        status: message.status,
        created_at: message.created_at,
    },
});


export const finalizeAssistantMessage = (message) => ({
    type: CONTRACT_MESSAGE_STREAM_DONE,
    payload: {
        id: message.id,
        content: message.content,
        sender: "bot",
        status: message.status,
        created_at: message.created_at,
    },
});

export const contractMessageError = (error) => ({
    type: CONTRACT_MESSAGE_ERROR,
    payload: error,
});

// Rich execution tracking action creators
export const contractChatUpdateMessage = (messageId, updates) => ({
    type: CONTRACT_CHAT_UPDATE_MESSAGE,
    payload: { id: messageId, ...updates },
});

export const contractChatUpdateTools = (messageId, toolData) => ({
    type: CONTRACT_CHAT_UPDATE_TOOLS,
    payload: { messageId, ...toolData },
});

export const contractChatAddToolExecution = (messageId, toolExecution) => ({
    type: CONTRACT_CHAT_ADD_TOOL_EXECUTION,
    payload: { messageId, toolExecution },
});

export const contractChatUpdateNodeExecution = (messageId, node_executions) => ({
    type: CONTRACT_CHAT_UPDATE_NODE_EXECUTION,
    payload: { messageId, node_executions },
});

export const contractChatUpdatePlan = (messageId, execution_plan) => ({
    type: CONTRACT_CHAT_UPDATE_PLAN,
    payload: { messageId, execution_plan },
});

export const contractChatUpdateStepExecution = (messageId, step_id, updates) => ({
    type: CONTRACT_CHAT_UPDATE_STEP_EXECUTION,
    payload: { messageId, step_id, updates },
});

export const contractChatUpdateSources = (messageId, sources) => ({
    type: CONTRACT_CHAT_UPDATE_SOURCES,
    payload: { messageId, sources },
});

export const contractChatUpdateMetadata = (messageId, metadata) => ({
    type: CONTRACT_CHAT_UPDATE_METADATA,
    payload: { messageId, metadata },
});

export const updateEditStatus = (messageId, editId, status) => ({
    type: CONTRACT_CHAT_UPDATE_EDIT_STATUS,
    payload: { messageId, editId, status },
});

export const updateReviewSuggestionStatus = (messageId, suggestionId, status, markId = null) => ({
    type: CONTRACT_CHAT_UPDATE_REVIEW_SUGGESTION_STATUS,
    payload: { messageId, suggestionId, status, mark_id: markId },
});


/**
 * Thunk to send a user message to the backend and stream back the assistant response via SSE.
 * Uses the new agentic chat engine with named SSE events and rich execution tracking.
 */
export const sendContractMessage = (
    conversationId,
    contractId,
    userId,
    userMessage,
    country = 'MA'
) => async (dispatch, getState) => {
    try {
        const tokenPayload = backendClient.getToken();
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // 1) Immediately dispatch the user's message locally
        dispatch(addUserMessage(userMessage));

        // 2) Create a rich assistant message upfront
        const assistantMsgId = `assistant-${Date.now()}`;
        const assistantMsg = {
            id: assistantMsgId,
            content: '',
            sender: 'bot',
            status: 'PENDING',
            is_streaming: true,
            created_at: new Date().toISOString(),
            sources: [],
            tool_executions: [],
            activeTools: [],
            completedTools: [],
            node_executions: [],
            execution_plan: null,
            metadata: null,
            workflowProgress: 0,
            currentThought: '',
            document_edits: [],
        };
        dispatch(streamAssistantUpdate(assistantMsg));

        const token = `${tokenPayload.token_type
            .charAt(0)
            .toUpperCase()}${tokenPayload.token_type.slice(1)} ${encodeURIComponent(
                tokenPayload.access_token
            )}`;

        // 3) Build the SSE URL for the contract chat engine
        const currentLang = i18n.language || 'fr';
        const params = new URLSearchParams({
            contract_id: contractId,
            user_id: userId,
            user_message: userMessage.replace(/<br\s*\/?>/gi, '\n').trim(),
            country: country,
            doc_types: getDocTypesForCountry(country),
            ui_language: currentLang.split('-')[0],
        });
        const fullUrl = `${backendUrl}api/contract/chat/conversations/${conversationId}/message/stream?${params}`;

        // 4) Open the SSE connection
        const eventSource = new EventSourcePolyfill(fullUrl, {
            headers: {
                Authorization: token,
                Connection: 'keep-alive',
                'X-Timezone': timezone,
                'Accept-Language': currentLang.split('-')[0],
            },
            heartbeatTimeout: 1200000
        });

        // Local accumulators
        let accumulatedContent = '';
        let nodeExecutions = [];
        let executionPlan = null;

        // Helper to get current message from state
        const getCurrentMsg = () => {
            const state = getState();
            return (state.contract.messages || []).find(m => m.id === assistantMsgId);
        };

        // --- Node execution events ---
        eventSource.addEventListener('node_start', (event) => {
            const data = JSON.parse(event.data);
            nodeExecutions.push({
                node_name: data.node_name,
                status: 'started',
                description: data.description,
                started_at: data.timestamp,
                completed_at: null,
            });
            dispatch(contractChatUpdateNodeExecution(assistantMsgId, [...nodeExecutions]));
        });

        eventSource.addEventListener('node_complete', (event) => {
            const data = JSON.parse(event.data);
            nodeExecutions = nodeExecutions.map(node =>
                node.node_name === data.node_name && node.status === 'started'
                    ? {
                        ...node,
                        status: 'completed',
                        completed_at: data.timestamp,
                        reasoning: data.reasoning || null,
                        result_metadata: data.metadata || null,
                    }
                    : node
            );
            dispatch(contractChatUpdateNodeExecution(assistantMsgId, [...nodeExecutions]));
        });

        // --- Plan events ---
        eventSource.addEventListener('plan_created', (event) => {
            const data = JSON.parse(event.data);
            executionPlan = {
                steps: data.steps.map(step => ({
                    ...step,
                    status: 'pending',
                    started_at: null,
                    completed_at: null,
                })),
                strategy: data.strategy,
                created_at: data.timestamp,
            };
            dispatch(contractChatUpdatePlan(assistantMsgId, executionPlan));
        });

        eventSource.addEventListener('step_start', (event) => {
            const data = JSON.parse(event.data);
            dispatch(contractChatUpdateStepExecution(assistantMsgId, data.step_id, {
                status: 'in_progress',
                started_at: data.timestamp,
            }));
        });

        eventSource.addEventListener('step_complete', (event) => {
            const data = JSON.parse(event.data);
            dispatch(contractChatUpdateStepExecution(assistantMsgId, data.step_id, {
                status: data.status,
                completed_at: data.timestamp,
            }));
        });

        // --- Tool events ---
        eventSource.addEventListener('tool_start', (event) => {
            const data = JSON.parse(event.data);
            const currentMsg = getCurrentMsg();

            dispatch(contractChatUpdateTools(assistantMsgId, {
                activeTools: [...(currentMsg?.activeTools || []), data.tool_name],
            }));

            dispatch(contractChatAddToolExecution(assistantMsgId, {
                event_id: `${data.tool_name}-${Date.now()}`,
                tool_name: data.tool_name,
                status: 'started',
                started_at: data.timestamp,
                parameters: data.parameters,
            }));
        });

        eventSource.addEventListener('tool_progress', () => {
            // Optional progress tracking
        });

        eventSource.addEventListener('tool_complete', (event) => {
            const data = JSON.parse(event.data);
            const currentMsg = getCurrentMsg();

            const updatedExecutions = (currentMsg?.tool_executions || []).map(exec =>
                exec.tool_name === data.tool_name && exec.status === 'started'
                    ? {
                        ...exec,
                        status: data.success ? 'completed' : 'failed',
                        completed_at: data.timestamp,
                        execution_time_ms: data.execution_time_ms,
                        result: data.result,
                        success: data.success,
                    }
                    : exec
            );

            dispatch(contractChatUpdateTools(assistantMsgId, {
                activeTools: (currentMsg?.activeTools || []).filter(t => t !== data.tool_name),
                completedTools: [...(currentMsg?.completedTools || []), data.tool_name],
                tool_executions: updatedExecutions,
            }));
        });

        eventSource.addEventListener('tool_error', (event) => {
            const data = JSON.parse(event.data);
            const currentMsg = getCurrentMsg();
            dispatch(contractChatUpdateTools(assistantMsgId, {
                activeTools: (currentMsg?.activeTools || []).filter(t => t !== data.tool_name),
            }));
        });

        // --- Sources ---
        eventSource.addEventListener('sources', (event) => {
            const data = JSON.parse(event.data);
            dispatch(contractChatUpdateSources(assistantMsgId, data.sources || []));
        });

        // --- Document edits ---
        eventSource.addEventListener('document_edits', (event) => {
            const data = JSON.parse(event.data);
            const edits = (data.edits || []).map(edit => ({
                ...edit,
                status: 'pending',
            }));
            dispatch({
                type: CONTRACT_CHAT_UPDATE_EDITS,
                payload: { messageId: assistantMsgId, edits },
            });
        });

        // --- Document review: progress per section ---
        eventSource.addEventListener('review_section_start', (event) => {
            const data = JSON.parse(event.data);
            dispatch({
                type: CONTRACT_CHAT_UPDATE_REVIEW_PROGRESS,
                payload: {
                    messageId: assistantMsgId,
                    update: {
                        type: 'section_start',
                        section_id: data.section_id,
                        section_title: data.section_title,
                        section_index: data.section_index,
                        total_sections: data.total_sections,
                    },
                },
            });
        });

        eventSource.addEventListener('review_section_complete', (event) => {
            const data = JSON.parse(event.data);
            dispatch({
                type: CONTRACT_CHAT_UPDATE_REVIEW_PROGRESS,
                payload: {
                    messageId: assistantMsgId,
                    update: {
                        type: 'section_complete',
                        section_id: data.section_id,
                        section_title: data.section_title,
                        suggestion_count: data.suggestion_count,
                        suggestions: (data.suggestions || []).map(s => ({
                            ...s,
                            status: 'pending',
                        })),
                    },
                },
            });
        });

        // --- Document review: final batch of track-change suggestions ---
        eventSource.addEventListener('document_suggestions', (event) => {
            const data = JSON.parse(event.data);
            const suggestions = (data.suggestions || []).map(s => ({
                ...s,
                status: 'pending',
            }));
            dispatch({
                type: CONTRACT_CHAT_UPDATE_REVIEW_SUGGESTIONS,
                payload: {
                    messageId: assistantMsgId,
                    suggestions,
                    review_summary: data.review_summary || null,
                },
            });
        });

        // --- Content streaming ---
        eventSource.addEventListener('content_chunk', (event) => {
            const data = JSON.parse(event.data);
            accumulatedContent += data.content;
            dispatch(contractChatUpdateMessage(assistantMsgId, { content: accumulatedContent, text: accumulatedContent }));
        });

        // --- Final answer ---
        eventSource.addEventListener('final_answer', (event) => {
            const data = JSON.parse(event.data);
            const finalContent = data.answer || data.content || accumulatedContent;
            dispatch(contractChatUpdateMessage(assistantMsgId, {
                content: finalContent,
                text: finalContent,
                is_streaming: false,
                status: 'SUCCESS',
            }));

            if (data.metadata) {
                dispatch(contractChatUpdateMetadata(assistantMsgId, data.metadata));
            }

            eventSource.close();
        });

        // --- Thought ---
        eventSource.addEventListener('thought', (event) => {
            const data = JSON.parse(event.data);
            dispatch(contractChatUpdateMessage(assistantMsgId, { currentThought: data.thought }));
        });

        // --- Error events ---
        eventSource.addEventListener('error', (event) => {
            if (event.data) {
                try {
                    const data = JSON.parse(event.data);
                    dispatch(contractChatUpdateMessage(assistantMsgId, {
                        content: data.error || 'An error occurred',
                        text: data.error || 'An error occurred',
                        is_streaming: false,
                        status: 'ERROR',
                    }));
                } catch {
                    dispatch(contractMessageError('An error occurred'));
                }
            }
            eventSource.close();
        });

        eventSource.addEventListener('title_update', () => {
            // No-op for contract chat
        });

        eventSource.addEventListener('heartbeat', () => {
            // Keep-alive
        });

        eventSource.onopen = () => {
            console.log("[Contract Chat] SSE connection opened.");
        };

        eventSource.onerror = (err) => {
            console.error("[Contract Chat] SSE connection error:", err);
            if (accumulatedContent === '') {
                dispatch(contractMessageError('Connection error'));
            }
            dispatch(contractChatUpdateMessage(assistantMsgId, { is_streaming: false }));
            eventSource.close();
        };

        return eventSource;
    } catch (err) {
        console.error("sendContractMessage error", err);
        dispatch(contractMessageError(err.message || 'An error occurred while sending message'));
    }
};

/**
 * Create a new conversation for chat
 */
export const createContractConversation = (contractId, userId) => async (dispatch) => {
    dispatch({ type: CREATE_CONTRACT_CONVERSATION_REQUEST });

    try {
        const response = await backendClient.createContractConversation(contractId, userId);


        if (response && response.conversation_id) {
            dispatch({
                type: CREATE_CONTRACT_CONVERSATION_SUCCESS,
                payload: response.conversation_id
            });

            return response
        } else {
            throw new Error('Invalid response format. Missing conversation ID.');
        }
    } catch (error) {
        console.error('Error creating conversation:', error);
        dispatch({
            type: CREATE_CONTRACT_CONVERSATION_FAILURE,
            payload: error.message || 'Failed to create conversation'
        });
        throw error;
    }
};

/**
 * Clear all messages from a conversation
 */
export const clearChat = (conversationId) => async (dispatch) => {
    dispatch({ type: CLEAR_CHAT_REQUEST });

    try {
        await backendClient.clearContractConversation(conversationId);

        dispatch({ type: CLEAR_CHAT_SUCCESS });
        dispatch(clearContractMessages());

        return true;
    } catch (error) {
        console.error('Error clearing chat:', error);
        dispatch({
            type: CLEAR_CHAT_FAILURE,
            payload: error.message || 'Failed to clear chat'
        });
        throw error;
    }
};

///////// adapt payload


// Helper functions
const convertNewlinesToBreaks = (text) => {
    if (!text) return "";
    return text.replace(/\n/g, "<br/>");
};

// Action to set contract highlight for risk analysis
export const setHighlightError = (errorText) => ({
    type: SET_HIGHLIGHT_ERROR,
    payload: errorText
});

// Action to reset risk analysis state
export const resetRiskAnalysis = () => ({
    type: RISK_ANALYSIS_RESET
});

// Action to add a risk to the state
export const addRiskAnalysis = (risk) => ({
    type: RISK_ANALYSIS_ADD,
    payload: risk
});

// Action to set risk analysis error
export const riskAnalysisError = (error) => ({
    type: RISK_ANALYSIS_ERROR,
    payload: error
});

// Action to mark risk analysis as complete
export const riskAnalysisComplete = () => ({
    type: RISK_ANALYSIS_COMPLETE
});

// Action to start risk analysis and set loadingRisk to true
export const riskAnalysisRequest = () => ({
    type: RISK_ANALYSIS_REQUEST
});

// Async action to get risk analysis via SSE
export const getRiskAnalysis = (contractId) => async (dispatch) => {
    try {
        console.log('Starting risk analysis for contract:', contractId);
        
        // Start loading state
        dispatch(riskAnalysisRequest());

        // Clear any existing risks before starting a new stream
        dispatch(resetRiskAnalysis());

        const tokenPayload = backendClient.getToken();
        console.log(tokenPayload, "tokenPayload")
        const token =
            `${tokenPayload.token_type.charAt(0).toUpperCase()}${tokenPayload.token_type.slice(1)} ` +
            `${encodeURIComponent(tokenPayload.access_token)}`;

        // Build the SSE URL
        const fullUrl = `${backendUrl}api/contract/risk-analysis/${contractId}`;
        console.log('Risk analysis URL:', fullUrl);

        // Use the centralized EventSource manager
        const lang = i18n.language || 'fr';
        const { connectionId, eventSource } = eventSourceManager.createConnection(
            fullUrl,
            {
                headers: {
                    Authorization: token,
                    Connection: 'keep-alive',
                    'Accept-Language': lang,
                }
            },
            'risk-analysis'
        );

        console.log('Created risk analysis connection:', connectionId);

        eventSource.onmessage = (event) => {
            if (!event.data) {
                console.warn('Received empty risk analysis data');
                return;
            }

            try {
                const riskPayload = JSON.parse(event.data);

                // Build the risk object; note the inclusion of risk_id
                const risk = {
                    risk_id: riskPayload.risk_id,
                    // Determine risk level if provided; default to 'Low Risk'
                    level: riskPayload.risk_level
                        ? (riskPayload.risk_level === 'High' ? 'High Risk'
                            : (riskPayload.risk_level === 'Moderate' ? 'Medium Risk' : 'Low Risk'))
                        : 'Low Risk',
                    // Only update fields if they exist in the payload
                    erreur: riskPayload.excerpt ? convertNewlinesToBreaks(riskPayload.excerpt) : undefined,
                    details: convertNewlinesToBreaks(riskPayload.observations) || null,
                    suggestion: convertNewlinesToBreaks(riskPayload.mitigation_suggestion) || null,
                    sources: riskPayload.sources || [], // Include sources
                    status: riskPayload.status,
                };

                console.log('Dispatching risk analysis update:', risk);
                // Dispatch the same action; the reducer will handle merging
                dispatch(addRiskAnalysis(risk));
            } catch (err) {
                console.error('Error parsing risk analysis data:', err);
            }
        };

        eventSource.onopen = () => {
            console.log('Risk analysis SSE connection opened successfully.');
        };

        eventSource.onerror = (error) => {
            console.error('Risk analysis SSE error:', error);
            dispatch(riskAnalysisError(error.message || 'Failed to connect to risk analysis service'));
            eventSourceManager.closeConnection(connectionId);
            dispatch(riskAnalysisComplete());
        };

        // Add event handler for when the stream is done
        eventSource.addEventListener('done', () => {
            console.log('Risk analysis stream complete');
            eventSourceManager.closeConnection(connectionId);
            dispatch(riskAnalysisComplete());
        });
        
        return { connectionId, eventSource };

    } catch (err) {
        console.error('Risk analysis error:', err);
        dispatch(riskAnalysisError(err.message || 'An error occurred while fetching risk analysis.'));
        dispatch(riskAnalysisComplete());
    }
};


// Action to update contract status (optimistic)
export const updateContractStatus = (contractId, status, previousStatus, content) => async (dispatch) => {
    // Optimistic update — move card immediately
    dispatch({
        type: UPDATE_CONTRACT_STATUS_SUCCESS,
        payload: { contractId, status }
    });

    try {
        await backendClient.updateContract(contractId, { status, content });
    } catch (error) {
        console.error('Failed to update contract status', error);

        // Revert to previous status on failure
        dispatch({
            type: UPDATE_CONTRACT_STATUS_SUCCESS,
            payload: { contractId, status: previousStatus }
        });

        throw error;
    }
};

// Update a contract's country (optimistic). Persists to the backend and
// reverts Redux state if the request fails.
export const updateContractCountry = (contractId, country, previousCountry) => async (dispatch) => {
    dispatch({
        type: UPDATE_CONTRACT_COUNTRY_SUCCESS,
        payload: { contractId, country }
    });

    try {
        await backendClient.updateContract(contractId, { country });
    } catch (error) {
        console.error('Failed to update contract country', error);
        dispatch({
            type: UPDATE_CONTRACT_COUNTRY_SUCCESS,
            payload: { contractId, country: previousCountry }
        });
        throw error;
    }
};

// Optimistic kanban reorder — updates positions (and optionally status) in Redux,
// then persists to backend. Reverts on failure.
export const reorderKanbanContracts = (updates, previousContracts) => async (dispatch) => {
    // Optimistic update
    dispatch({ type: REORDER_KANBAN_CONTRACTS, payload: updates });

    try {
        await backendClient.reorderKanbanContracts(updates);
    } catch (error) {
        console.error('Failed to reorder kanban contracts', error);
        // Revert — restore previous positions
        const revertUpdates = previousContracts.map(c => ({
            id: c.id,
            kanban_position: c.kanban_position,
            status: c.status,
        }));
        dispatch({ type: REORDER_KANBAN_CONTRACTS, payload: revertUpdates });
        throw error;
    }
};


////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////
export const clausesRequest = () => ({
    type: CLAUSES_REQUEST
});
// Action to reset risk analysis state
export const resetClauses = () => ({
    type: CLAUSES_RESET
});

// Action to add a risk to the state
export const addClause = (clause) => ({
    type: CLAUSES_ADD,
    payload: clause
});

// Action to set risk analysis error
export const clauseError = (error) => ({
    type: CLAUSES_ERROR,
    payload: error
});

// Action to mark risk analysis as complete
export const clauseComplete = () => ({
    type: CLAUSES_COMPLETE
});



// Async action to get risk analysis via SSE
export const getClauses = (contractId) => async (dispatch) => {
    try {
        // Start loading state
        dispatch(clausesRequest());

        // Clear any existing risks before starting a new stream
        dispatch(resetClauses());

        const tokenPayload = backendClient.getToken();
        const token =
            `${tokenPayload.token_type.charAt(0).toUpperCase()}${tokenPayload.token_type.slice(1)} ` +
            `${encodeURIComponent(tokenPayload.access_token)}`;

        // Build the SSE URL
        const fullUrl = `${backendUrl}api/contract/extract-clause/${contractId}`;

        // Use the centralized EventSource manager
        const clauseLang = i18n.language || 'fr';
        const { connectionId, eventSource } = eventSourceManager.createConnection(
            fullUrl,
            {
                headers: {
                    Authorization: token,
                    Connection: 'keep-alive',
                    'Accept-Language': clauseLang,
                }
            },
            'extract-clause'
        );

        eventSource.onmessage = (event) => {
            if (!event.data) {
                console.warn('Received empty clause explanation data');
                return;
            }

            try {
                const clausePayload = JSON.parse(event.data);

                const clause = {
                    title: convertNewlinesToBreaks(clausePayload.title),
                    description: convertNewlinesToBreaks(clausePayload.description),
                    text: convertNewlinesToBreaks(clausePayload.text),
                };

                console.log('Dispatching clause:', clause);
                // Dispatch the risk to add it to the state
                dispatch(addClause(clause));
            } catch (err) {
                console.error('Error parsing risk analysis data:', err);
            }
        };

        eventSource.onopen = () => {
            console.log('Clause extraction SSE connection opened successfully.');
        };

        eventSource.onerror = (error) => {
            console.error('Clause extraction SSE error:', error);
            dispatch(clauseError(error.message || 'Failed to connect to clause extraction service'));
            eventSourceManager.closeConnection(connectionId);
            dispatch(clauseComplete());
        };

        // Add event handler for when the stream is done
        eventSource.addEventListener('done', () => {
            console.log('Clause extraction stream complete');
            eventSourceManager.closeConnection(connectionId);
            dispatch(clauseComplete());
        });
        
        return { connectionId, eventSource };
    } catch (err) {
        console.error('Clause error:', err);
        dispatch(clauseError(err.message || 'An error occurred while fetching risk analysis.'));
        dispatch(clauseComplete());
    }
};


/////////////////////////////////////////
/////////////////////////////////////////
/////////////////////////////////////////

export const SuggestedClausesRequest = () => ({
    type: SUGGESTED_CLAUSES_REQUEST
});
// Action to reset risk analysis state
export const resetSuggestedClauses = () => ({
    type: SUGGESTED_CLAUSES_RESET
});

// Action to add a risk to the state
export const addSuggestedClause = (clause) => ({
    type: SUGGESTED_CLAUSES_ADD,
    payload: clause
});

// Action to set risk analysis error
export const SuggestedClauseError = (error) => ({
    type: SUGGESTED_CLAUSES_ERROR,
    payload: error
});

// Action to mark risk analysis as complete
export const SuggestedClauseComplete = () => ({
    type: SUGGESTED_CLAUSES_COMPLETE
});



// Async action to get risk analysis via SSE
export const getSuggestedClauses = (contractId) => async (dispatch) => {
    try {
        // Start loading state
        dispatch(SuggestedClausesRequest());

        // Clear any existing risks before starting a new stream
        dispatch(resetSuggestedClauses());

        const tokenPayload = backendClient.getToken();
        const token =
            `${tokenPayload.token_type.charAt(0).toUpperCase()}${tokenPayload.token_type.slice(1)} ` +
            `${encodeURIComponent(tokenPayload.access_token)}`;

        // Build the SSE URL
        const fullUrl = `${backendUrl}api/contract/suggestions/${contractId}`;

        // Use the centralized EventSource manager
        const suggestionsLang = i18n.language || 'fr';
        const { connectionId, eventSource } = eventSourceManager.createConnection(
            fullUrl,
            {
                headers: {
                    Authorization: token,
                    Connection: 'keep-alive',
                    'Accept-Language': suggestionsLang,
                }
            },
            'suggestions'
        );

        eventSource.onmessage = (event) => {
            if (!event.data) {
                console.warn('Received empty clause explanation data');
                return;
            }

            try {
                const clausePayload = JSON.parse(event.data);

                const clause = {
                    title: convertNewlinesToBreaks(clausePayload.title),
                    description: convertNewlinesToBreaks(clausePayload.description),
                    definition: convertNewlinesToBreaks(clausePayload.definition),
                };

                console.log('Dispatching clause:', clause);
                // Dispatch the risk to add it to the state
                dispatch(addSuggestedClause(clause));
            } catch (err) {
                console.error('Error parsing suggested clause data:', err);
            }
        };

        eventSource.onopen = () => {
            console.log('Clause suggestions SSE connection opened successfully.');
        };

        eventSource.onerror = (error) => {
            console.error('Clause suggestions SSE error:', error);
            dispatch(SuggestedClauseError(error.message || 'Failed to connect to clause suggestions service'));
            eventSourceManager.closeConnection(connectionId);
            dispatch(SuggestedClauseComplete());
        };

        // Add event handler for when the stream is done
        eventSource.addEventListener('done', () => {
            console.log('Clause suggestions stream complete');
            eventSourceManager.closeConnection(connectionId);
            dispatch(SuggestedClauseComplete());
        });
        
        return { connectionId, eventSource };
    } catch (err) {
        console.error('Clause error:', err);
        dispatch(SuggestedClauseError(err.message || 'An error occurred while fetching clause recommandations.'));
        dispatch(SuggestedClauseComplete());
    }
};



export const filterContracts = (filterParams, limit = 10, offset = 0) => async (dispatch) => {
    try {
        dispatch({ type: FILTER_CONTRACTS_REQUEST });

        const filteredContracts = await backendClient.filterContracts(filterParams, limit, offset);

        dispatch({
            type: FILTER_CONTRACTS_SUCCESS,
            payload: filteredContracts
        });

        return filteredContracts;
    } catch (error) {
        console.error('Error filtering contracts:', error);
        dispatch({
            type: FILTER_CONTRACTS_FAILURE,
            payload: error.message || 'Failed to filter contracts'
        });
        throw error;
    }
};

// Action to update a contract with optimistic UI updates
export const handleUpdateContract = (contractId, contractData) => async (dispatch, getState) => {
    try {
        // Get current state
        const state = getState();
        const originalContract = state.contract.contracts.find(c => c.id === contractId);

        // Dispatch optimistic update action
        dispatch({
            type: UPDATE_CONTRACT_OPTIMISTIC,
            payload: {
                id: contractId,
                contractData
            }
        });

        // Perform the actual API call in the background
        const updatedContract = await backendClient.updateContract(contractId, contractData);

        // Refresh contracts in the background to ensure consistency
        // Only if we're updating more than just the title
        if (Object.keys(contractData).length > 1 || !contractData.hasOwnProperty('title')) {
            const userId = state.auth?.user?.user?.id;
            if (userId) {
                // We don't need to await this, let it happen in the background
                dispatch(fetchContracts(userId, 10, 0, true));
            }
        }

        return updatedContract;
    } catch (error) {
        console.error('Error updating contract:', error);

        // If the update fails, revert the optimistic update
        const state = getState();
        const originalContract = state.contract.contracts.find(c => c.id === contractId);

        if (originalContract) {
            // Create a revert action with the original data
            // Only revert the properties that were attempted to be updated
            const revertData = {};
            Object.keys(contractData).forEach(key => {
                revertData[key] = originalContract[key];
            });

            dispatch({
                type: UPDATE_CONTRACT_OPTIMISTIC,
                payload: {
                    id: contractId,
                    contractData: revertData
                }
            });
        }

        throw error;
    }
};

// Contract Categories Actions
export const fetchContractCategories = () => async (dispatch) => {
    try {
        dispatch({ type: FETCH_CONTRACT_CATEGORIES_REQUEST });

        const categories = await backendClient.getContractCategories();

        dispatch({
            type: FETCH_CONTRACT_CATEGORIES_SUCCESS,
            payload: categories
        });

        return categories;
    } catch (error) {
        console.error('Error fetching contract categories:', error);
        dispatch({
            type: FETCH_CONTRACT_CATEGORIES_FAILURE,
            payload: error.message || 'Failed to fetch contract categories'
        });
        throw error;
    }
};


// ── Key Info Actions ────────────────────────────────────────────────────────

export const keyInfoExtractComplete = () => ({
    type: KEY_INFO_EXTRACT_COMPLETE,
});

export const fetchKeyInfo = (contractId) => async (dispatch) => {
    try {
        dispatch({ type: KEY_INFO_REQUEST });
        const response = await fetch(
            `${backendUrl}api/contract/key-info/${contractId}`,
            { headers: backendClient.authHeader() }
        );
        if (!response.ok) {
            if (response.status === 404) {
                dispatch({ type: KEY_INFO_FETCH_SUCCESS, payload: null });
                return null;
            }
            throw new Error('Failed to fetch key info');
        }
        const data = await response.json();
        dispatch({ type: KEY_INFO_FETCH_SUCCESS, payload: data });
        return data;
    } catch (error) {
        console.error('Error fetching key info:', error);
        dispatch({ type: KEY_INFO_ERROR, payload: error.message });
    }
};

export const extractKeyInfo = (contractId) => async (dispatch) => {
    try {
        dispatch({ type: KEY_INFO_EXTRACT_REQUEST });

        const tokenPayload = backendClient.getToken();
        const token =
            `${tokenPayload.token_type.charAt(0).toUpperCase()}${tokenPayload.token_type.slice(1)} ` +
            `${encodeURIComponent(tokenPayload.access_token)}`;

        const fullUrl = `${backendUrl}api/contract/key-info/${contractId}/extract`;
        const lang = i18n.language || 'fr';

        const { connectionId, eventSource } = eventSourceManager.createConnection(
            fullUrl,
            {
                headers: {
                    Authorization: token,
                    Connection: 'keep-alive',
                    'Accept-Language': lang,
                }
            },
            'key-info-extract'
        );

        eventSource.onmessage = (event) => {
            if (!event.data) return;
            try {
                const payload = JSON.parse(event.data);
                if (payload.status === 'completed' && payload.data) {
                    dispatch({ type: KEY_INFO_EXTRACT_UPDATE, payload: payload.data });
                }
            } catch (err) {
                console.error('Key info SSE parse error:', err);
            }
        };

        eventSource.onerror = (error) => {
            console.error('Key info SSE error:', error);
            dispatch({ type: KEY_INFO_ERROR, payload: 'Extraction failed' });
            eventSourceManager.closeConnection(connectionId);
            dispatch(keyInfoExtractComplete());
        };

        eventSource.addEventListener('done', () => {
            eventSourceManager.closeConnection(connectionId);
            dispatch(keyInfoExtractComplete());
        });

        return { connectionId, eventSource };

    } catch (err) {
        console.error('Key info extraction error:', err);
        dispatch({ type: KEY_INFO_ERROR, payload: err.message });
        dispatch(keyInfoExtractComplete());
    }
};

export const updateKeyInfoField = (contractId, fieldData) => async (dispatch) => {
    try {
        const response = await fetch(
            `${backendUrl}api/contract/key-info/${contractId}`,
            {
                method: 'PUT',
                headers: backendClient.authHeader(),
                body: JSON.stringify(fieldData),
            }
        );
        if (!response.ok) throw new Error('Failed to update key info');
        const data = await response.json();
        dispatch({ type: KEY_INFO_UPDATE_FIELD, payload: data });
        return data;
    } catch (error) {
        console.error('Error updating key info:', error);
        dispatch({ type: KEY_INFO_ERROR, payload: error.message });
    }
};

// ── Law Dependencies Actions ──────────────────────────────────────────────

export const fetchLawDependencies = (contractId) => async (dispatch) => {
    try {
        dispatch({ type: LAW_DEPS_REQUEST });
        const response = await fetch(
            `${backendUrl}api/contract/law-dependencies/${contractId}`,
            { headers: backendClient.authHeader() }
        );
        if (!response.ok) {
            if (response.status === 404) {
                dispatch({ type: LAW_DEPS_SUCCESS, payload: [] });
                return [];
            }
            throw new Error('Failed to fetch law dependencies');
        }
        const data = await response.json();
        dispatch({ type: LAW_DEPS_SUCCESS, payload: data });
        return data;
    } catch (error) {
        console.error('Error fetching law dependencies:', error);
        dispatch({ type: LAW_DEPS_ERROR, payload: error.message });
    }
};

// ── Regulatory Alerts Actions ─────────────────────────────────────────────

export const fetchRegulatoryAlerts = (contractId) => async (dispatch) => {
    try {
        dispatch({ type: REG_ALERTS_REQUEST });
        const response = await fetch(
            `${backendUrl}api/contract/monitoring/regulatory-alerts/${contractId}`,
            { headers: backendClient.authHeader() }
        );
        if (!response.ok) {
            if (response.status === 404) {
                dispatch({ type: REG_ALERTS_SUCCESS, payload: [] });
                return [];
            }
            throw new Error('Failed to fetch regulatory alerts');
        }
        const data = await response.json();
        dispatch({ type: REG_ALERTS_SUCCESS, payload: data });
        return data;
    } catch (error) {
        console.error('Error fetching regulatory alerts:', error);
        dispatch({ type: REG_ALERTS_ERROR, payload: error.message });
    }
};

export const updateAlertStatus = (alertId, status) => async (dispatch) => {
    try {
        const response = await fetch(
            `${backendUrl}api/contract/monitoring/regulatory-alerts/${alertId}`,
            {
                method: 'PUT',
                headers: backendClient.authHeader(),
                body: JSON.stringify({ status }),
            }
        );
        if (!response.ok) throw new Error('Failed to update alert status');
        const data = await response.json();
        dispatch({ type: REG_ALERT_UPDATE, payload: data });
        // Also refresh monitoring alerts list if loaded
        dispatch(fetchMonitoringAlerts());
        return data;
    } catch (error) {
        console.error('Error updating alert status:', error);
    }
};

// ── Workspaces & Folders Actions ────────────────────────────────────────

export const fetchWorkspaces = () => async (dispatch) => {
    try {
        dispatch({ type: WORKSPACES_REQUEST });
        const res = await backendClient.get('api/contract/workspace/workspaces');
        const data = await res.json();
        dispatch({ type: WORKSPACES_SUCCESS, payload: data });
        return data;
    } catch (error) {
        console.error('Error fetching workspaces:', error);
        dispatch({ type: WORKSPACES_FAILURE, payload: error.message });
    }
};

export const fetchFolders = (workspaceId) => async (dispatch) => {
    try {
        dispatch({ type: FOLDERS_REQUEST });
        const res = await backendClient.get(`api/contract/workspace/workspaces/${workspaceId}/folders`);
        const data = await res.json();
        dispatch({ type: FOLDERS_SUCCESS, payload: data });
        return data;
    } catch (error) {
        console.error('Error fetching folders:', error);
        dispatch({ type: FOLDERS_FAILURE, payload: error.message });
    }
};

export const fetchSmartViews = () => async (dispatch) => {
    try {
        dispatch({ type: SMART_VIEWS_REQUEST });
        const res = await backendClient.get('api/contract/workspace/smart-views');
        const data = await res.json();
        dispatch({ type: SMART_VIEWS_SUCCESS, payload: data });
        return data;
    } catch (error) {
        console.error('Error fetching smart views:', error);
        dispatch({ type: SMART_VIEWS_FAILURE, payload: error.message });
    }
};

export const createWorkspace = (data) => async (dispatch) => {
    try {
        const res = await backendClient.post('api/contract/workspace/workspaces', data);
        const workspace = await res.json();
        dispatch({ type: CREATE_WORKSPACE_SUCCESS, payload: workspace });
        return workspace;
    } catch (error) {
        console.error('Error creating workspace:', error);
        throw error;
    }
};

export const createFolder = (data) => async (dispatch) => {
    try {
        const res = await backendClient.post('api/contract/workspace/folders', data);
        const folder = await res.json();
        dispatch({ type: CREATE_FOLDER_SUCCESS, payload: folder });
        return folder;
    } catch (error) {
        console.error('Error creating folder:', error);
        throw error;
    }
};

export const moveContract = (contractId, workspaceId, folderId) => async (dispatch) => {
    try {
        await backendClient.put(`api/contract/workspace/contracts/${contractId}/move`, {
            workspace_id: workspaceId,
            folder_id: folderId,
        });
    } catch (error) {
        console.error('Error moving contract:', error);
        throw error;
    }
};

export const createSmartView = (data) => async (dispatch) => {
    try {
        const res = await backendClient.post('api/contract/workspace/smart-views', data);
        const smartView = await res.json();
        dispatch({ type: CREATE_SMART_VIEW_SUCCESS, payload: smartView });
        return smartView;
    } catch (error) {
        console.error('Error creating smart view:', error);
        throw error;
    }
};

export const deleteSmartView = (id) => async (dispatch) => {
    try {
        await backendClient.delete(`api/contract/workspace/smart-views/${id}`);
        dispatch({ type: DELETE_SMART_VIEW_SUCCESS, payload: id });
    } catch (error) {
        console.error('Error deleting smart view:', error);
        throw error;
    }
};

export const fetchSmartViewCounts = () => async (dispatch) => {
    try {
        const res = await backendClient.get('api/contract/smart-view-counts');
        const data = await res.json();
        dispatch({ type: SMART_VIEW_COUNTS_SUCCESS, payload: data });
    } catch (error) {
        console.error('Error fetching smart view counts:', error);
    }
};

export const setActiveWorkspace = (workspace) => ({
    type: SET_ACTIVE_WORKSPACE,
    payload: workspace,
});

export const setActiveFolder = (folder) => ({
    type: SET_ACTIVE_FOLDER,
    payload: folder,
});

export const setActiveSmartViewAction = (viewId, filterKey = null) => ({
    type: SET_ACTIVE_SMART_VIEW,
    payload: viewId,
    meta: { filterKey },
});

// ── Workspace & Folder Management Actions ──────────────────────────────

export const renameWorkspace = (workspaceId, name) => async (dispatch) => {
    try {
        await backendClient.put(`api/contract/workspace/workspaces/${workspaceId}`, { name });
        dispatch(fetchWorkspaces());
    } catch (error) {
        console.error('Error renaming workspace:', error);
        throw error;
    }
};

export const deleteWorkspace = (workspaceId) => async (dispatch) => {
    try {
        await backendClient.delete(`api/contract/workspace/workspaces/${workspaceId}`);
        dispatch(fetchWorkspaces());
    } catch (error) {
        console.error('Error deleting workspace:', error);
        throw error;
    }
};

export const renameFolder = (folderId, name, workspaceId) => async (dispatch) => {
    try {
        await backendClient.put(`api/contract/workspace/folders/${folderId}`, { name });
        if (workspaceId) {
            dispatch(fetchFolders(workspaceId));
        }
    } catch (error) {
        console.error('Error renaming folder:', error);
        throw error;
    }
};

export const deleteFolder = (folderId, workspaceId) => async (dispatch) => {
    try {
        await backendClient.delete(`api/contract/workspace/folders/${folderId}`);
        if (workspaceId) {
            dispatch(fetchFolders(workspaceId));
        }
    } catch (error) {
        console.error('Error deleting folder:', error);
        throw error;
    }
};

export const moveFolder = (folderId, parentId, workspaceId) => async (dispatch) => {
    try {
        await backendClient.put(`api/contract/workspace/folders/${folderId}/move`, { parent_id: parentId });
        if (workspaceId) {
            dispatch(fetchFolders(workspaceId));
        }
    } catch (error) {
        console.error('Error moving folder:', error);
        throw error;
    }
};


// ── Monitoring Actions ──────────────────────────────────────────────────

export const setActiveMonitoringView = (view) => ({
    type: SET_ACTIVE_MONITORING_VIEW,
    payload: view, // 'regulatory_alerts' | 'contract_lifecycle' | null
});

export const fetchMonitoringAlerts = ({ status, impact_level, search } = {}) => async (dispatch) => {
    try {
        dispatch({ type: FETCH_MONITORING_ALERTS_REQUEST });
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (impact_level) params.set('impact_level', impact_level);
        if (search) params.set('search', search);
        const qs = params.toString();
        const url = `api/contract/monitoring/regulatory-alerts${qs ? `?${qs}` : ''}`;
        const res = await backendClient.get(url);
        const data = await res.json();
        dispatch({ type: FETCH_MONITORING_ALERTS_SUCCESS, payload: data });
        return data;
    } catch (error) {
        console.error('Error fetching monitoring alerts:', error);
        dispatch({ type: FETCH_MONITORING_ALERTS_FAILURE, payload: error.message });
        throw error;
    }
};

export const fetchMonitoringLifecycle = ({ search, lifecycle_status } = {}) => async (dispatch) => {
    try {
        dispatch({ type: FETCH_MONITORING_LIFECYCLE_REQUEST });
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (lifecycle_status) params.set('lifecycle_status', lifecycle_status);
        const qs = params.toString();
        const url = `api/contract/monitoring/lifecycle${qs ? `?${qs}` : ''}`;
        const res = await backendClient.get(url);
        const data = await res.json();
        dispatch({ type: FETCH_MONITORING_LIFECYCLE_SUCCESS, payload: data });
        return data;
    } catch (error) {
        console.error('Error fetching monitoring lifecycle:', error);
        dispatch({ type: FETCH_MONITORING_LIFECYCLE_FAILURE, payload: error.message });
        throw error;
    }
};

export const createAmendment = (contractId) => async () => {
    try {
        const res = await backendClient.post(`api/contract/${contractId}/amendment`, {});
        return await res.json();
    } catch (error) {
        console.error('Error creating amendment:', error);
        throw error;
    }
};

export const upsertRenewalAlert = (data) => async () => {
    try {
        const res = await backendClient.post('api/contract/monitoring/renewal-alert', data);
        return await res.json();
    } catch (error) {
        console.error('Error saving renewal alert:', error);
        throw error;
    }
};

export const getRenewalAlert = (contractId) => async () => {
    try {
        const res = await backendClient.get(`api/contract/monitoring/renewal-alert/${contractId}`);
        return await res.json();
    } catch (error) {
        console.error('Error fetching renewal alert:', error);
        return null;
    }
};

export const deleteRenewalAlert = (contractId) => async () => {
    try {
        await backendClient.delete(`api/contract/monitoring/renewal-alert/${contractId}`);
        return true;
    } catch (error) {
        console.error('Error deleting renewal alert:', error);
        throw error;
    }
};

export const generateAISuggestion = (contractId, alertId, clauseText) => async () => {
    try {
        const res = await backendClient.post('api/contract/monitoring/generate-suggestion', {
            contract_id: contractId,
            alert_id: alertId,
            clause_text: clauseText,
        });
        return await res.json();
    } catch (error) {
        console.error('Error generating AI suggestion:', error);
        throw error;
    }
};